/**
 * PurchaseOrdersTab.js — Purchase Order list + editor for a project.
 *
 * Views:
 *  'list'   — all POs for this project (status filter, open/delete)
 *  'editor' — full A4-landscape-style editor with dual-column header,
 *             item table, notes, export, send
 *
 * Features:
 *  • Auto-fill company info from brandConfig
 *  • Supplier autocomplete (SupplierPicker) with override
 *  • Item table: image, description, link, qty, unit, unit_price, line_total
 *  • Notes textarea at bottom
 *  • Status badge & update
 *  • Export XLSX (server-side) + PDF (jsPDF, A4 Landscape)
 *  • Send hook (marks status = 'sent')
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, ArrowLeft, Save, Trash2, Loader2, Send,
  Download, FileSpreadsheet, FileDown, ChevronDown,
  Image as ImageIcon, Link as LinkIcon, X,
  Building2, User, Phone, Mail, MapPin, FileText,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../lib/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ItemImageUpload from './ItemImageUpload';
import SupplierPicker from './SupplierPicker';
import {
  getDefaultCompanyInfo, getLOGO_URL,
  loadImageAsBase64,
} from '../../lib/brandConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => n == null ? '' : Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtC = (n) => `$${fmt(n)}`;
const UNITS = ['ea', 'm', 'm²', 'm³', 'kg', 't', 'l', 'hr', 'day', 'wk', 'lot', 'set', 'box', 'roll'];

const STATUS_STYLES = {
  draft:     { bg: '#f3f4f6', color: '#6b7280', label: 'Draft' },
  sent:      { bg: '#dbeafe', color: '#1e40af', label: 'Sent' },
  approved:  { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  cancelled: { bg: '#fee2e2', color: '#991b1b', label: 'Cancelled' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

const newLineItem = () => ({
  id: crypto.randomUUID(),
  seq: 0, image_url: '', description: '', link: '',
  quantity: 1, unit: 'ea', unit_price: 0, line_total: 0,
});

// ── Main export ───────────────────────────────────────────────────────────────
export default function PurchaseOrdersTab({ project }) {
  const projectId = project?.id;
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [pos, setPos] = useState([]);
  const [activePo, setActivePo] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const exportRef = useRef(null);

  // Close export dropdown on outside click
  useEffect(() => {
    const h = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Load list ───────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    if (!projectId) return;
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/purchase-orders`);
      setPos(res.data || []);
    } catch (err) {
      console.error('Failed to load purchase orders:', err);
      toast.error(apiErrorMessage(err, 'Could not load purchase orders.'));
    }
    setLoadingList(false);
  }, [projectId]);

  useEffect(() => { loadList(); }, [loadList]);

  // ── New PO ──────────────────────────────────────────────────────────────────
  const handleNewPo = () => {
    const ci = getDefaultCompanyInfo();
    setActivePo({
      _isNew: true,
      po_number: '',
      date: new Date().toISOString().split('T')[0],
      project_name: project?.project_name || '',
      company_info: {
        name: ci.name, acn: ci.acn, address: ci.address,
        phone: ci.phone, director: ci.director, logo_url: ci.logo_url || getLOGO_URL(),
      },
      supplier_id: '',
      supplier_info: { name: '', abn: '', contact_person: '', address: '', phone: '', email: '' },
      items: [newLineItem()],
      notes: '',
      status: 'draft',
      supplier_email: '',
    });
    setView('editor');
  };

  // ── Open existing ───────────────────────────────────────────────────────────
  const handleOpenPo = async (poId) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/purchase-orders/${poId}`);
      setActivePo({ ...res.data, _isNew: false });
      setView('editor');
    } catch (err) {
      alert('Failed to load PO: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!activePo) return;
    setSaving(true);
    try {
      const { _isNew, id, created_at, updated_at, company_id, project_id, deleted_at, ...payload } = activePo;
      // Recalculate line totals
      payload.items = (payload.items || []).map(it => ({
        ...it,
        line_total: Math.round(parseFloat(it.quantity || 0) * parseFloat(it.unit_price || 0) * 100) / 100,
      }));

      let saved;
      if (_isNew) {
        const res = await axios.post(`${API}/projects/${projectId}/purchase-orders`, payload);
        saved = res.data;
      } else {
        const res = await axios.put(`${API}/projects/${projectId}/purchase-orders/${id}`, payload);
        saved = res.data;
      }
      setActivePo({ ...saved, _isNew: false });
      await loadList();
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (poId) => {
    if (!window.confirm('Delete this Purchase Order?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/purchase-orders/${poId}`);
      await loadList();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ── Send ────────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!activePo?.id) { alert('Please save first.'); return; }
    if (!window.confirm(`Mark PO ${activePo.po_number} as Sent?`)) return;
    try {
      await axios.post(`${API}/projects/${projectId}/purchase-orders/${activePo.id}/send`);
      setActivePo(prev => ({ ...prev, status: 'sent' }));
      await loadList();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ── Export XLSX ─────────────────────────────────────────────────────────────
  const handleExportXlsx = async () => {
    if (!activePo?.id) { alert('Please save first.'); return; }
    try {
      const res = await axios.get(
        `${API}/projects/${projectId}/purchase-orders/${activePo.id}/export?format=xlsx`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activePo.po_number || 'PO'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.detail || err.message));
    }
    setShowExport(false);
  };

  // ── Export PDF (jsPDF A4 Landscape) ─────────────────────────────────────────
  const handleExportPdf = useCallback(async () => {
    if (!activePo) return;
    const po = activePo;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pdfW = doc.internal.pageSize.getWidth(); // 297
    const pdfH = doc.internal.pageSize.getHeight(); // 210

    const NAVY = [26, 26, 46];
    const GOLD = [201, 168, 76];
    const WHITE = [255, 255, 255];
    const ci = po.company_info || {};
    const si = po.supplier_info || {};

    // ── Header block ─────────────────────────────────────────────────────────
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, pdfW, 34, 'F');

    // Left: company logo + info
    let logoLoaded = false;
    if (ci.logo_url) {
      try {
        const b64 = await fetch(ci.logo_url).then(r => r.blob()).then(blob => new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        }));
        doc.addImage(b64, 'PNG', 12, 5, 22, 22);
        logoLoaded = true;
      } catch (_) {}
    }
    const textX = logoLoaded ? 38 : 12;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GOLD);
    doc.text(ci.name || '', textX, 12);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.setTextColor(...WHITE);
    const ciLines = [
      ci.acn ? `ACN: ${ci.acn}` : null,
      ci.address || null,
      ci.phone ? `Ph: ${ci.phone}` : null,
    ].filter(Boolean);
    ciLines.forEach((line, i) => doc.text(line, textX, 18 + i * 5));

    // Right: PO title + details
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.setTextColor(...GOLD);
    doc.text('PURCHASE ORDER', pdfW - 12, 12, { align: 'right' });

    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.setTextColor(200, 200, 200);
    const details = [
      `PO Number: ${po.po_number || '—'}`,
      `Date: ${po.date || '—'}`,
      `Project: ${po.project_name || '—'}`,
    ];
    details.forEach((d, i) => doc.text(d, pdfW - 12, 19 + i * 5, { align: 'right' }));

    // Supplier info block (right side, below details)
    if (si.name) {
      doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.setTextColor(...GOLD);
      doc.text('SUPPLIER', pdfW - 12, 38, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(44, 44, 44);
      const siLines = [
        si.name,
        si.abn ? `ABN: ${si.abn}` : null,
        si.contact_person || null,
        si.address || null,
        si.phone || null,
        si.email || null,
      ].filter(Boolean);
      siLines.forEach((line, i) => doc.text(line, pdfW - 12, 44 + i * 5, { align: 'right' }));
    }

    // ── Items table ─────────────────────────────────────────────────────────
    const items = po.items || [];
    const subtotal = items.reduce((s, it) => s + (it.line_total || 0), 0);
    const gst = subtotal * 0.1;
    const total = subtotal + gst;

    autoTable(doc, {
      startY: 40,
      head: [['#', 'Description', 'Qty', 'Unit', 'Unit Price', 'Total']],
      body: items.map((it, i) => [
        i + 1,
        it.description || '',
        it.quantity || 0,
        it.unit || 'ea',
        fmtC(it.unit_price),
        fmtC(it.line_total),
      ]),
      foot: [
        ['', '', '', '', 'Subtotal (excl. GST)', fmtC(subtotal)],
        ['', '', '', '', 'GST (10%)', fmtC(gst)],
        ['', '', '', '', 'TOTAL incl. GST', fmtC(total)],
      ],
      headStyles: { fillColor: NAVY, textColor: WHITE, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [44, 44, 44] },
      footStyles: { fontSize: 9, fontStyle: 'bold', textColor: NAVY },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 14, halign: 'right' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 28, halign: 'right' },
        5: { cellWidth: 32, halign: 'right', fontStyle: 'bold' },
      },
      didParseCell: (data) => {
        if (data.section === 'foot' && data.row.index === 2) {
          data.cell.styles.fillColor = NAVY;
          data.cell.styles.textColor = GOLD;
          data.cell.styles.fontSize = 10;
        }
      },
      margin: { left: 12, right: 12 },
      alternateRowStyles: { fillColor: [248, 247, 244] },
    });

    // ── Notes ────────────────────────────────────────────────────────────────
    const finalY = doc.lastAutoTable.finalY + 8;
    if (po.notes && finalY < pdfH - 20) {
      doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 46);
      doc.text('Notes:', 12, finalY);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(44, 44, 44);
      doc.setFillColor(255, 251, 235);
      doc.rect(12, finalY + 3, pdfW - 24, 20, 'F');
      const noteLines = doc.splitTextToSize(po.notes, pdfW - 30);
      doc.text(noteLines, 14, finalY + 9);
    }

    // ── Footer: signature line ───────────────────────────────────────────────
    doc.setFontSize(7); doc.setTextColor(150, 150, 150);
    doc.text(`${ci.name || ''} · ${po.po_number || ''} · ${po.date || ''}`, 12, pdfH - 6);
    doc.text('Authorised: ___________________________', pdfW - 12, pdfH - 6, { align: 'right' });

    doc.save(`${po.po_number || 'PO'}_${(si.name || '').replace(/\s+/g, '_')}.pdf`);
    setShowExport(false);
  }, [activePo]);

  // ── Field helpers ───────────────────────────────────────────────────────────
  const setPoField = (field, value) => setActivePo(prev => ({ ...prev, [field]: value }));
  const setCompanyField = (f, v) => setActivePo(prev => ({ ...prev, company_info: { ...prev.company_info, [f]: v } }));
  const setSupplierField = (f, v) => setActivePo(prev => ({ ...prev, supplier_info: { ...prev.supplier_info, [f]: v } }));
  const setItemField = (id, f, v) => setActivePo(prev => ({
    ...prev,
    items: prev.items.map(it => it.id === id ? { ...it, [f]: v } : it),
  }));
  const addItem = () => setActivePo(prev => ({ ...prev, items: [...prev.items, newLineItem()] }));
  const removeItem = (id) => setActivePo(prev => ({ ...prev, items: prev.items.filter(it => it.id !== id) }));

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  if (view === 'list') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#1a1a2e' }}>
            Purchase Orders
          </h3>
          <button
            onClick={handleNewPo}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded font-semibold"
            style={{ background: '#c9a84c', color: '#1a1a2e' }}
          >
            <Plus className="w-3.5 h-3.5" /> New PO
          </button>
        </div>

        {loadingList ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#c9a84c' }} />
          </div>
        ) : pos.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: 'rgba(201,168,76,0.3)' }} />
            <p className="text-sm text-[#9ca3af]">No purchase orders yet.</p>
            <button onClick={handleNewPo} className="mt-3 text-sm underline" style={{ color: '#c9a84c' }}>Create the first PO</button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg" style={{ border: '1px solid #e5e4df' }}>
            {/* Header */}
            <div
              className="grid px-4 py-2 text-xs font-bold uppercase tracking-wider"
              style={{ gridTemplateColumns: '90px 1fr 1fr 100px 80px 48px', background: '#1a1a2e', color: '#c9a84c' }}
            >
              <span>PO #</span><span>Supplier</span><span>Project</span><span>Date</span><span>Status</span><span></span>
            </div>
            {pos.map((po, idx) => (
              <div
                key={po.id}
                className="grid px-4 py-3 items-center hover:bg-[#f3f2ef] cursor-pointer transition-colors"
                style={{
                  gridTemplateColumns: '90px 1fr 1fr 100px 80px 48px',
                  borderTop: idx > 0 ? '1px solid #f0efe9' : 'none',
                }}
                onClick={() => handleOpenPo(po.id)}
              >
                <span className="font-mono text-sm font-semibold" style={{ color: '#1a1a2e' }}>{po.po_number}</span>
                <span className="text-sm text-[#374151] truncate">{po.supplier_info?.name || '—'}</span>
                <span className="text-sm text-[#6b7280] truncate">{po.project_name || project?.project_name || '—'}</span>
                <span className="text-sm text-[#6b7280] font-mono">{po.date || '—'}</span>
                <StatusBadge status={po.status} />
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(po.id); }}
                  className="p-1 rounded text-[#d1d5db] hover:text-[#dc2626] hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── EDITOR VIEW ──────────────────────────────────────────────────────────────
  if (!activePo) return null;
  const items = activePo.items || [];
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.quantity || 0) * parseFloat(it.unit_price || 0)), 0);
  const gst = subtotal * 0.1;
  const total = subtotal + gst;

  return (
    <div className="space-y-4">
      {/* Editor toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setView('list')}
          className="flex items-center gap-1.5 text-sm"
          style={{ color: '#6b7280' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to List
        </button>
        <span className="text-[#d1d5db]">|</span>
        <span className="font-mono font-semibold text-sm" style={{ color: '#1a1a2e' }}>
          {activePo.po_number || 'New PO'}
        </span>
        <StatusBadge status={activePo.status} />

        <div className="ml-auto flex items-center gap-2">
          {saving && <span className="text-xs text-[#9ca3af] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>}

          {/* Status selector */}
          <select
            value={activePo.status}
            onChange={e => setPoField('status', e.target.value)}
            className="px-2 py-1.5 text-xs border rounded font-semibold"
            style={{ borderColor: '#e5e4df' }}
          >
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="approved">Approved</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Export */}
          <div className="relative" ref={exportRef}>
            <button
              onClick={() => setShowExport(!showExport)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border"
              style={{ borderColor: '#e5e4df', color: '#6b7280' }}
            >
              <Download className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3 h-3" />
            </button>
            {showExport && (
              <div className="absolute right-0 top-full mt-1 rounded-lg shadow-xl z-40 overflow-hidden" style={{ background: '#fff', border: '1px solid #e5e4df', width: 180 }}>
                <button onClick={handleExportXlsx} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-[#f3f2ef]">
                  <FileSpreadsheet className="w-4 h-4" style={{ color: '#c9a84c' }} /> Excel (XLSX)
                </button>
                <button onClick={handleExportPdf} className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-[#f3f2ef]">
                  <FileDown className="w-4 h-4" style={{ color: '#c9a84c' }} /> PDF (A4 Landscape)
                </button>
              </div>
            )}
          </div>

          {/* Send */}
          <button
            onClick={handleSend}
            disabled={!activePo.id || activePo.status === 'sent'}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border font-semibold disabled:opacity-40"
            style={{ borderColor: '#0369a1', color: '#0369a1' }}
            title="Mark as Sent (email integration coming soon)"
          >
            <Send className="w-3.5 h-3.5" /> Send
          </button>

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm rounded font-semibold disabled:opacity-40"
            style={{ background: '#c9a84c', color: '#1a1a2e' }}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>

      {/* ── PO Document ─────────────────────────────────────────────────────── */}
      <div className="rounded-xl overflow-hidden shadow-lg" style={{ border: '1px solid #e5e4df' }}>

        {/* HEADER: Company (left) + Supplier (right) */}
        <div className="grid grid-cols-2" style={{ background: '#1a1a2e' }}>
          {/* Company side */}
          <div className="p-5 border-r" style={{ borderColor: 'rgba(201,168,76,0.2)' }}>
            <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#c9a84c' }}>Company (auto-filled)</p>
            <div className="space-y-2">
              <EditableHeaderField label="Company Name" value={activePo.company_info?.name || ''} onChange={v => setCompanyField('name', v)} dark />
              <EditableHeaderField label="ACN" value={activePo.company_info?.acn || ''} onChange={v => setCompanyField('acn', v)} dark />
              <EditableHeaderField label="Address" value={activePo.company_info?.address || ''} onChange={v => setCompanyField('address', v)} dark />
              <EditableHeaderField label="Phone" value={activePo.company_info?.phone || ''} onChange={v => setCompanyField('phone', v)} dark />
              <EditableHeaderField label="Director" value={activePo.company_info?.director || ''} onChange={v => setCompanyField('director', v)} dark />
            </div>
          </div>

          {/* Supplier side */}
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: '#c9a84c' }}>Supplier / Subcontractor</p>
            </div>
            <div className="mb-3">
              <SupplierPicker
                value={activePo.supplier_info?.name || ''}
                supplierId={activePo.supplier_id}
                onSelect={({ id, name, email, phone, address }) => {
                  setPoField('supplier_id', id);
                  setActivePo(prev => ({
                    ...prev,
                    supplier_id: id,
                    supplier_info: { ...prev.supplier_info, name, email, phone: phone || '', address: address || '' },
                    supplier_email: email,
                  }));
                }}
                placeholder="Search or select supplier…"
              />
            </div>
            <div className="space-y-2">
              <EditableHeaderField label="Name / Company" value={activePo.supplier_info?.name || ''} onChange={v => setSupplierField('name', v)} dark />
              <EditableHeaderField label="ABN" value={activePo.supplier_info?.abn || ''} onChange={v => setSupplierField('abn', v)} dark />
              <EditableHeaderField label="Contact Person" value={activePo.supplier_info?.contact_person || ''} onChange={v => setSupplierField('contact_person', v)} dark />
              <EditableHeaderField label="Address" value={activePo.supplier_info?.address || ''} onChange={v => setSupplierField('address', v)} dark />
              <EditableHeaderField label="Phone" value={activePo.supplier_info?.phone || ''} onChange={v => setSupplierField('phone', v)} dark />
              <EditableHeaderField label="Email" value={activePo.supplier_info?.email || ''} onChange={v => { setSupplierField('email', v); setPoField('supplier_email', v); }} dark />
            </div>
          </div>
        </div>

        {/* PO meta bar */}
        <div
          className="flex items-center gap-6 px-5 py-3"
          style={{ background: 'rgba(201,168,76,0.08)', borderBottom: '1px solid rgba(201,168,76,0.2)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#9ca3af] uppercase">PO Number</span>
            <input
              type="text"
              value={activePo.po_number}
              onChange={e => setPoField('po_number', e.target.value)}
              placeholder="PO-001"
              className="font-mono font-bold text-sm px-2 py-1 border rounded"
              style={{ borderColor: '#e5e4df', width: 100 }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#9ca3af] uppercase">Date</span>
            <input
              type="date"
              value={activePo.date}
              onChange={e => setPoField('date', e.target.value)}
              className="text-sm px-2 py-1 border rounded"
              style={{ borderColor: '#e5e4df' }}
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[#9ca3af] uppercase">Project</span>
            <span className="text-sm text-[#374151]">{activePo.project_name || project?.project_name}</span>
          </div>
        </div>

        {/* Items table */}
        <div className="overflow-auto">
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 700 }}>
            <colgroup>
              <col style={{ width: 50 }} />
              <col style={{ width: 56 }} />
              <col style={{ width: 'auto' }} />
              <col style={{ width: 48 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 70 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 40 }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f3f2ef', borderBottom: '2px solid #c9a84c' }}>
                {['#', 'Image', 'Description / Notes', 'Link', 'Qty', 'Unit', 'Unit Price', 'Line Total', ''].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#6b7280' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <POItemRow
                  key={item.id}
                  item={item}
                  idx={idx}
                  setItemField={setItemField}
                  removeItem={removeItem}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Add item */}
        <div className="px-4 py-2" style={{ borderTop: '1px solid #f0efe9' }}>
          <button onClick={addItem} className="flex items-center gap-1.5 text-sm" style={{ color: '#c9a84c' }}>
            <Plus className="w-3.5 h-3.5" /> Add item
          </button>
        </div>

        {/* Totals */}
        <div className="flex justify-end px-4 py-3 gap-8" style={{ borderTop: '1px solid #e5e4df', background: '#f8f7f4' }}>
          <div className="text-right space-y-1">
            <div className="flex justify-between gap-12 text-sm text-[#6b7280]">
              <span>Subtotal (excl. GST)</span>
              <span className="font-mono font-semibold text-[#374151]">{fmtC(subtotal)}</span>
            </div>
            <div className="flex justify-between gap-12 text-sm text-[#6b7280]">
              <span>GST (10%)</span>
              <span className="font-mono text-[#374151]">{fmtC(gst)}</span>
            </div>
            <div className="flex justify-between gap-12 text-sm font-bold" style={{ color: '#1a1a2e' }}>
              <span>Total incl. GST</span>
              <span className="font-mono text-lg" style={{ color: '#c9a84c' }}>{fmtC(total)}</span>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="px-5 py-4" style={{ borderTop: '2px solid rgba(201,168,76,0.2)', background: '#fffbeb' }}>
          <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#92400e' }}>
            Notes / Delivery Instructions
          </label>
          <textarea
            value={activePo.notes || ''}
            onChange={e => setPoField('notes', e.target.value)}
            rows={4}
            placeholder="e.g. Please deliver to site between 7am–3pm. Contact site manager on arrival. All materials must comply with AS standards."
            className="w-full px-3 py-2 text-sm border rounded resize-y"
            style={{ borderColor: '#fde68a', background: '#fffef7', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
          />
        </div>
      </div>
    </div>
  );
}

// ── Editable header field (dark bg) ──────────────────────────────────────────
function EditableHeaderField({ label, value, onChange, dark }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs min-w-[90px]" style={{ color: dark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>{label}</span>
      {editing ? (
        <input
          autoFocus
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={() => { onChange(draft); setEditing(false); }}
          onKeyDown={e => { if (e.key === 'Enter') { onChange(draft); setEditing(false); } }}
          className="flex-1 px-2 py-0.5 text-sm rounded"
          style={{ background: dark ? 'rgba(255,255,255,0.15)' : '#fff', color: dark ? '#fff' : '#1a1a2e', border: '1px solid rgba(201,168,76,0.5)' }}
        />
      ) : (
        <span
          className="flex-1 text-sm px-2 py-0.5 rounded cursor-text hover:bg-white/10 truncate"
          style={{ color: dark ? (value ? '#fff' : 'rgba(255,255,255,0.3)') : (value ? '#374151' : '#9ca3af') }}
          onClick={() => setEditing(true)}
        >
          {value || <span style={{ opacity: 0.4 }}>Click to edit</span>}
        </span>
      )}
    </div>
  );
}

// ── PO Item row ───────────────────────────────────────────────────────────────
function POItemRow({ item, idx, setItemField, removeItem }) {
  const [imageModal, setImageModal] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [editingLink, setEditingLink] = useState(false);

  const lineTotal = Math.round(parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0) * 100) / 100;

  return (
    <tr style={{ background: idx % 2 === 0 ? '#fff' : '#f8f7f4', borderBottom: '1px solid #f0efe9' }}>
      {/* # */}
      <td className="px-3 py-2 text-center text-xs font-mono text-[#9ca3af]">{idx + 1}</td>

      {/* Image */}
      <td className="px-2 py-1.5 text-center">
        <button
          onClick={() => setImageModal(true)}
          className="flex items-center justify-center w-10 h-10 rounded border overflow-hidden hover:border-[#c9a84c] transition-colors mx-auto"
          style={{ borderColor: '#e5e4df' }}
        >
          {item.image_url
            ? <img src={item.image_url} alt="" className="w-full h-full object-cover" />
            : <ImageIcon className="w-4 h-4 text-[#d1d5db]" />
          }
        </button>
        {imageModal && (
          <ItemImageUpload
            currentUrl={item.image_url}
            onUpload={url => setItemField(item.id, 'image_url', url)}
            onClose={() => setImageModal(false)}
          />
        )}
      </td>

      {/* Description */}
      <td className="px-3 py-1.5">
        {editingDesc ? (
          <input
            autoFocus type="text" value={item.description}
            onChange={e => setItemField(item.id, 'description', e.target.value)}
            onBlur={() => setEditingDesc(false)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Tab') e.target.blur(); }}
            className="w-full px-2 py-1 text-sm border rounded"
            style={{ borderColor: '#c9a84c' }}
          />
        ) : (
          <span
            className="block text-sm cursor-text hover:bg-yellow-50 rounded px-1 py-0.5"
            style={{ color: item.description ? '#1a1a2e' : '#d1d5db' }}
            onClick={() => setEditingDesc(true)}
          >
            {item.description || 'Click to add description…'}
          </span>
        )}
      </td>

      {/* Link */}
      <td className="px-2 py-1.5 text-center">
        {editingLink ? (
          <input
            autoFocus type="url" defaultValue={item.link}
            onBlur={e => { setItemField(item.id, 'link', e.target.value); setEditingLink(false); }}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
            className="w-28 px-2 py-1 text-xs border rounded"
            style={{ borderColor: '#c9a84c' }}
            placeholder="https://…"
          />
        ) : item.link ? (
          <a href={item.link} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
            <LinkIcon className="w-3.5 h-3.5 mx-auto" style={{ color: '#3b82f6' }} />
          </a>
        ) : (
          <button onClick={() => setEditingLink(true)}>
            <LinkIcon className="w-3.5 h-3.5 mx-auto text-[#e5e4df] hover:text-[#3b82f6]" />
          </button>
        )}
      </td>

      {/* Qty */}
      <td className="px-2 py-1.5">
        <input
          type="number" value={item.quantity} min="0" step="0.01"
          onChange={e => setItemField(item.id, 'quantity', parseFloat(e.target.value) || 0)}
          className="w-full text-right text-sm px-2 py-1 border rounded font-mono"
          style={{ borderColor: '#e5e4df' }}
        />
      </td>

      {/* Unit */}
      <td className="px-2 py-1.5">
        <select
          value={item.unit || 'ea'}
          onChange={e => setItemField(item.id, 'unit', e.target.value)}
          className="w-full text-sm px-1 py-1 border rounded"
          style={{ borderColor: '#e5e4df' }}
        >
          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </td>

      {/* Unit price */}
      <td className="px-2 py-1.5">
        <input
          type="number" value={item.unit_price} min="0" step="0.01"
          onChange={e => setItemField(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
          className="w-full text-right text-sm px-2 py-1 border rounded font-mono"
          style={{ borderColor: '#e5e4df' }}
        />
      </td>

      {/* Line total */}
      <td className="px-3 py-1.5 text-right font-mono font-semibold text-sm" style={{ color: '#1a1a2e' }}>
        {fmtC(lineTotal)}
      </td>

      {/* Delete */}
      <td className="px-1 py-1.5 text-center">
        <button onClick={() => removeItem(item.id)} className="p-1 rounded text-[#d1d5db] hover:text-[#dc2626]">
          <X className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}
